import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BookingSummaryCard = ({ bookingData }) => {
  const formatIndianNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Booking Summary
        </h2>
        <div className="flex items-center space-x-2 text-success">
          <Icon name="Shield" size={16} />
          <span className="text-sm font-medium">SSL Secured</span>
        </div>
      </div>

      {/* Package Details */}
      <div className="space-y-4 mb-6">
        <div className="flex space-x-4">
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={bookingData.package.image}
              alt={bookingData.package.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-foreground mb-1">
              {bookingData.package.name}
            </h3>
            <div className="flex items-center text-muted-foreground text-sm mb-2">
              <Icon name="MapPin" size={14} className="mr-1" />
              <span>{bookingData.package.location}</span>
            </div>
            <div className="flex items-center text-muted-foreground text-sm">
              <Icon name="Calendar" size={14} className="mr-1" />
              <span>{bookingData.package.duration} days</span>
            </div>
          </div>
        </div>
      </div>

      {/* Travel Details */}
      <div className="border-t border-border pt-4 mb-6">
        <h4 className="font-heading font-medium text-foreground mb-3">Travel Details</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-muted-foreground mb-1">Check-in</div>
            <div className="font-medium text-foreground">
              {formatDate(bookingData.checkIn)}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Check-out</div>
            <div className="font-medium text-foreground">
              {formatDate(bookingData.checkOut)}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Travelers</div>
            <div className="font-medium text-foreground">
              {bookingData.travelers.adults} Adults
              {bookingData.travelers.children > 0 && `, ${bookingData.travelers.children} Children`}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Room Type</div>
            <div className="font-medium text-foreground">
              {bookingData.roomType}
            </div>
          </div>
        </div>
      </div>

      {/* Traveler Information */}
      <div className="border-t border-border pt-4 mb-6">
        <h4 className="font-heading font-medium text-foreground mb-3">Primary Traveler</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Icon name="User" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{bookingData.traveler.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Mail" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{bookingData.traveler.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Phone" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{bookingData.traveler.phone}</span>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="border-t border-border pt-4">
        <h4 className="font-heading font-medium text-foreground mb-3">Price Breakdown</h4>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Package Cost</span>
            <span className="text-foreground">₹{formatIndianNumber(bookingData.pricing.packageCost)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Service Fee</span>
            <span className="text-foreground">₹{formatIndianNumber(bookingData.pricing.serviceFee)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">GST (18%)</span>
            <span className="text-foreground">₹{formatIndianNumber(bookingData.pricing.gst)}</span>
          </div>
          {bookingData.pricing.discount > 0 && (
            <div className="flex justify-between items-center text-success">
              <span>Discount Applied</span>
              <span>-₹{formatIndianNumber(bookingData.pricing.discount)}</span>
            </div>
          )}
          <div className="border-t border-border pt-3">
            <div className="flex justify-between items-center">
              <span className="font-heading font-semibold text-foreground text-lg">Total Amount</span>
              <span className="font-heading font-bold text-primary text-xl">
                ₹{formatIndianNumber(bookingData.pricing.total)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Reference */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground mb-1">Booking Reference</div>
            <div className="font-data font-medium text-foreground">
              {bookingData.bookingReference}
            </div>
          </div>
          <Icon name="Copy" size={16} className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors" />
        </div>
      </div>
    </div>
  );
};

export default BookingSummaryCard;